import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Footprints } from 'lucide-react';
import { api } from '../lib/api';
import { hoursText, minutes, openOn, place, today } from '../lib/format';
import { isBar, kindForStops, kindLabel } from '../lib/places';
import { useTrip } from '../state/trip';
import PlaceMap from '../components/PlaceMap';
import TripEndRow from '../components/TripEndRow';
import { ErrorNote, Spinner } from '../components/ui';

export default function RouteDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { replaceStops } = useTrip();
  const [selected, setSelected] = useState<string | null>(null);
  const route = useQuery({ queryKey: ['route', slug], queryFn: () => api.route(slug) });

  if (route.isLoading) return <div className="mx-auto max-w-7xl px-4 sm:px-6"><Spinner label="Loading route" /></div>;
  if (route.error || !route.data)
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <ErrorNote error={route.error}>That route doesn&apos;t exist.</ErrorNote>
      </div>
    );

  const r = route.data;
  const places = r.stops.map((s) => s.place);
  const day = today();

  return (
    <div className="mx-auto max-w-7xl px-4 pb-14 pt-10 sm:px-6">
      <Link to="/routes" className="text-sm">
        ← All routes
      </Link>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <p className="eyebrow">{r.region}</p>
          <h1 className="text-5xl font-black sm:text-6xl">{r.name}</h1>
          <p className="tabular-nums text-muted">
            {r.stops.length} stops · ~{r.estimatedMiles} mi · ~{minutes(r.estimatedMinutes)} driving{r.isLoop ? ' · loop' : ''}
          </p>
          {r.description && <p className="max-w-3xl text-lg text-[#d6d2c2]">{r.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              // All bars is a bar hop (it needs a driver); anything with bites is a day out.
              replaceStops(places, undefined, kindForStops(places));
              navigate('/my-trip');
            }}
          >
            Use this route for my trip
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_440px]">
        <PlaceMap
          stops={places}
          connectStops
          isLoop={r.isLoop}
          fitPoints={places.map((p) => [p.lat, p.lng])}
          fitKey={r.slug}
          selectedId={selected}
          onSelect={setSelected}
          className="h-[420px] lg:sticky lg:top-24 lg:h-[640px]"
          label={`Map of ${r.name}`}
        />

        <div className="flex flex-col gap-4">
          <section className="flex flex-col gap-2" aria-labelledby="routeEnds">
            <h2 id="routeEnds" className="text-2xl font-black">Your start and finish</h2>
            <p className="text-sm text-muted">Where you set off from and where you end up. They stay when you use this route for your trip.</p>
            <TripEndRow which="start" />
            <TripEndRow which="end" />
          </section>
          <ol className="flex flex-col">
            {r.stops.map((s) => {
              const open = openOn(s.place, day);
              return (
                <li key={s.place.id}>
                  <div
                    className={`card flex gap-4 p-4 ${selected === s.place.id ? 'border-amber' : ''}`}
                    onMouseEnter={() => setSelected(s.place.id)}
                  >
                    <span className="flex size-10 flex-none items-center justify-center rounded-full bg-amber text-lg font-extrabold text-amber-ink">
                      {s.number}
                    </span>
                    <div className="flex min-w-0 flex-col gap-1">
                      <Link to={`/stops/${s.place.slug}`} className="text-lg font-bold text-cream hover:text-amber-hi">
                        {s.place.name}
                      </Link>
                      <span className="text-sm text-muted">{[kindLabel(s.place.kind), place(s.place)].filter(Boolean).join(' · ')}</span>
                      {s.place.note && <p className="text-[15px] text-[#d6d2c2]">{s.place.note}</p>}
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`chip ${open === false ? 'chip-warn' : open ? 'chip-ok' : ''}`}>{hoursText(s.place, day)}</span>
                        {s.place.warning && <span className="chip chip-warn">{s.place.warning}</span>}
                        {isBar(s.place.kind) && <span className="chip chip-dd">DD perk: ask the bar</span>}
                      </div>
                    </div>
                  </div>
                  {s.leg && (
                    <div className="flex items-center gap-2 py-2 pl-8 text-sm tabular-nums text-muted">
                      {s.leg.walkable ? <Footprints size={14} aria-hidden /> : <span className="h-5 border-l-2 border-dashed border-amber" aria-hidden />}
                      {s.leg.walkable ? `Walk · ~${s.leg.miles} mi · ${s.leg.minutes} min` : `~${s.leg.miles} mi · ~${s.leg.minutes} min drive`}
                      {r.isLoop && s.number === r.stops.length ? ' back to the start' : ''}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}
